import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSpecPlReview, useSubmitSpecPlReview } from '../../../hooks/useSpecRequirements.js';
import { ApiError } from '../../../lib/api/_request.js';

export interface PlReviewPanelProps {
  /** 案件キー */
  pjKey: string;
}

/**
 * 操作できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.plReview.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.plReview.failed');
}

/**
 * 判断の呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-connects none
 */
function decisionLabel(t: TFunction, decision: string): string {
  if (decision === 'approved') return t('spec.plReview.decision.approved');
  if (decision === 'changes_requested') return t('spec.plReview.decision.changes_requested');
  return decision;
}

/**
 * 案件全体に対する PL の承認・差し戻し。取得と提出のフックはここで持つ（案件の画面が長いため）。
 *
 * 成果物ごとのレビュー（`ReviewActions`）と違い、案件の要件定義を**全体として**見て決める。
 * 誰が PL か（親チケットの担当者）の判定とコメントの検証は API が持つので、画面はボタンを出して
 * 返ったメッセージを見せるだけにする。差し戻しだけはコメントが要るので、空のときは押せない。
 *
 * **差し戻しのコメント欄は押しても空にしない。** 以前は押した時点で空にしていたので、権限
 * （403）や検証（400）で弾かれると書いた指摘がそのまま消え、同じ文章を書き直すことになっていた。
 * 送信の成否は `mutate` の時点では分からないため、成功したかどうかで分けるのではなく残す。
 * 差し戻しは履歴カードに記録が出るので、欄に残った文章を「まだ送れていない」と読み違える余地も無い。
 * 承認は空でも送れる任意のコメントなので、こちらは押したら空にする（判断ごとに書き換えるもの）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件全体に対する PL の承認・コメント付きの差し戻しと、その履歴を画面から行う
 * @graph-connects ApiError [references] 操作が失敗した理由の判別
 * @graph-connects useSpecPlReview [calls] PL レビューの取得
 * @graph-connects useSubmitSpecPlReview [calls] PL の判断の記録
 */
export function PlReviewPanel({ pjKey }: PlReviewPanelProps) {
  const { t } = useTranslation();
  const query = useSpecPlReview(pjKey);
  const submit = useSubmitSpecPlReview(pjKey);
  const review = query.data ?? null;
  const loadFailed = query.isError;
  const busy = submit.isPending;
  const error = submit.error;
  const onSubmit = submit.mutate;
  const [comment, setComment] = useState('');
  const trimmed = comment.trim();
  const status = review?.status ?? null;
  const entries = [...(review?.entries ?? [])].reverse();

  return (
    <section className="mt-6 border border-[var(--color-border)] rounded p-4">
      <h3 className="text-sm font-bold">{t('spec.plReview.heading')}</h3>
      {loadFailed ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-error)]">
          {t('spec.plReview.loadFailed')}
        </p>
      ) : (
        <p
          role="status"
          data-status={status ?? 'none'}
          className={`mt-2 text-sm rounded border px-3 py-2 ${statusClass(status)}`}
        >
          {status === null ? t('spec.plReview.status.none') : decisionStatus(t, status)}
        </p>
      )}

      <textarea
        className="mt-3 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
        rows={3}
        placeholder={t('spec.plReview.commentPlaceholder')}
        aria-label={t('spec.plReview.commentAria')}
        value={comment}
        onChange={(e) => {
          setComment(e.target.value);
        }}
      />
      <div className="mt-2 flex gap-2 flex-wrap">
        <button
          type="button"
          disabled={busy}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          onClick={() => {
            onSubmit({ decision: 'approved', comment: trimmed });
            setComment('');
          }}
        >
          {t('spec.plReview.approve')}
        </button>
        <button
          type="button"
          disabled={busy || trimmed === ''}
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          title={trimmed === '' ? t('spec.plReview.sendBackMissing') : undefined}
          onClick={() => {
            onSubmit({ decision: 'changes_requested', comment: trimmed });
          }}
        >
          {t('spec.plReview.sendBack')}
        </button>
      </div>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      {entries.length > 0 ? (
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-[var(--color-text-secondary)]">
            {t('spec.plReview.historyHeading')}
          </h4>
          <ul className="mt-2 space-y-2">
            {entries.map((e) => (
              <li key={`${e.at}-${e.actor}`} className="text-sm">
                <span className="font-semibold">{decisionLabel(t, e.decision)}</span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {' '}
                  ・ {e.actor} ・ {e.at}
                </span>
                {e.comment === '' ? null : <p className="mt-1 whitespace-pre-wrap">{e.comment}</p>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/**
 * 最後の判断の見た目。承認は緑・差し戻しは橙で囲み、ボタンを押した結果が一目で分かるようにする。
 * 知らない値は目立たせない（判断として扱わない）。
 * @graph-connects none
 */
function statusClass(status: string | null): string {
  if (status === 'approved') {
    return 'font-semibold border-[var(--color-success)] bg-[var(--color-success-solid)] text-[var(--color-success-text)]';
  }
  if (status === 'changes_requested') {
    return 'font-semibold border-[var(--color-warning)] bg-[var(--color-warning-solid)] text-[var(--color-warning-text)]';
  }
  return 'border-transparent text-[var(--color-text-secondary)]';
}

/**
 * 最後の判断の見出し。知らない値は API の返した値をそのまま出す。
 * @graph-connects none
 */
function decisionStatus(t: TFunction, status: string): string {
  if (status === 'approved') return t('spec.plReview.status.approved');
  if (status === 'changes_requested') return t('spec.plReview.status.changes_requested');
  return status;
}
